import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  LANGUAGE_FLAGS,
  SUPPORTED_LANGUAGE_COUNT,
  SUPPORTED_LANGUAGES,
  languageLandingPath,
} from '../data/supportedLanguages';
import {
  useLandingNamespace,
  useLanguageLanding,
} from '../context/LanguageLandingContext';
import { isSafariBrowser, scrollReveal } from '../utils/safariMotion';
import './LanguagesSection.css';

const LanguagesSection: React.FC = () => {
  const safari = isSafariBrowser();
  const { language } = useLanguageLanding();
  const ns = useLandingNamespace();
  const { t } = useTranslation(ns);

  return (
    <section id="languages" className="languages-section">
      <div className="languages-section-container">
        <motion.div
          className="languages-section-header"
          transition={{ duration: 0.8, ease: 'easeOut' }}
          {...scrollReveal(safari, { opacity: 0, y: 30 }, { opacity: 1, y: 0 }, 'section')}
        >
          <motion.h2
            className="languages-section-title"
            transition={{ duration: 0.6, delay: 0.1 }}
            {...scrollReveal(safari, { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 'nested')}
          >
            {t('languagesTitle')}
          </motion.h2>
          <motion.p
            className="languages-section-subtitle"
            transition={{ duration: 0.6, delay: 0.2 }}
            {...scrollReveal(safari, { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, 'nested')}
          >
            {t('languagesSubtitle', { count: SUPPORTED_LANGUAGE_COUNT })}
          </motion.p>
        </motion.div>

        <motion.ul
          className="languages-section-grid"
          transition={{ duration: 0.7, delay: 0.15 }}
          {...scrollReveal(safari, { opacity: 0, y: 24 }, { opacity: 1, y: 0 }, 'section')}
        >
          {SUPPORTED_LANGUAGES.map((item) => {
            const isCurrent = language?.slug === item.slug;
            return (
              <li key={item.lang} className="languages-section-item">
                {isCurrent ? (
                  <span className="languages-section-item-current">
                    <span className="languages-section-flag" aria-hidden>
                      {LANGUAGE_FLAGS[item.lang] ?? ''}
                    </span>
                    <span className="languages-section-name">{item.name}</span>
                  </span>
                ) : (
                  <Link
                    to={languageLandingPath(item)}
                    className="languages-section-item-link"
                  >
                    <span className="languages-section-flag" aria-hidden>
                      {LANGUAGE_FLAGS[item.lang] ?? ''}
                    </span>
                    <span className="languages-section-name">{item.name}</span>
                  </Link>
                )}
              </li>
            );
          })}
        </motion.ul>

        <motion.div
          className="languages-section-footer"
          transition={{ duration: 0.6, delay: 0.2 }}
          {...scrollReveal(safari, { opacity: 0, y: 16 }, { opacity: 1, y: 0 }, 'nested')}
        >
          <Link to="/languages" className="languages-section-link">
            {t('seeAllLanguages')}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default LanguagesSection;
